import { Controller, useFormContext, useFormState } from 'react-hook-form';
import BaseRadioGroup from './BaseRadioGroup';

function get(obj, path) {
  if (!obj) return undefined;
  return path.split('.').reduce((acc, key) => acc?.[key], obj);
}

/**
 * ControlledRadioGroup
 *
 * React Hook Form–controlled wrapper around BaseRadioGroup.
 * Binds the selected value and validation rules to form state.
 *
 * @param {Object} props
 * @param {string} props.name - React Hook Form field name.
 * @param {'row'|'column'} [props.orientation='row'] - Layout orientation for the radio buttons
 * @param {'row'|'column'} [props.wrapperOrientation='column'] - Layout orientation for the Form Field Wrapper
 * @param {Object} [props.wrapperSx] - MUI sx styles applied to the FormFieldWrapper.
 * @param {Object} [props.wrapperVariant] - Form Field wrapper label variant
 * @param {Object} [props.rules] - React Hook Form validation rules.
 * @param {boolean} [props.showErrorInline=true] - Controls whether validation errors are rendered inline by the field.
 *
 * Passthrough props:
 * All other props are forwarded to BaseRadioGroup.
 *
 * Notes:
 * - Field value is treated as a scalar.
 * - Group-level errors can be rendered inline or delegated to a parent
 *   ErrorSummary by setting `showErrorInline={false}`.
 *
 * @returns {React.ReactElement}i
 */
export default function ControlledRadioGroup(props: any) {
  const { name, rules, showErrorInline = true, ...rest } = props;
  const { control, clearErrors } = useFormContext();

  const { errors } = useFormState({ name });
  const error = get(errors, name);

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field }) => (
        <BaseRadioGroup
          {...rest}
          name={name}
          value={field.value}
          onChange={(value) => {
            field.onChange(value);
            if (error) clearErrors(name);
          }}
          error={showErrorInline && Boolean(error)}
          errorMessage={showErrorInline ? error?.message : undefined}
        />
      )}
    />
  );
}
